import * as React from 'react';
import Link from 'next/link';
import { cn } from '@/util/cn';
import { Heading } from '@/components/ui/heading';
import { Text } from '@/components/ui/text';

// Editorial news tile used on home (Latest News carousel), the global
// /skireport page, and any future news index. One shape:
//   - 4:3 cover photo with hover zoom
//   - "CATEGORY · DATE" eyebrow above the title (both optional)
//   - bold title
//   - 2-line excerpt
//
// Wraps in next/link when `href` is provided. Image lazy-loading is
// the caller's job — pass an empty string to render the placeholder.
//
// Source pattern: promoted from the redesign home prototype so the
// production News surfaces share one implementation.

export type NewsCardProps = {
  title: string;
  /** Plain-text excerpt. Rendered with a 2-line clamp. */
  excerpt?: string;
  /** Cover photo. Pass an empty string to show the placeholder. */
  image: string;
  /** Category tag rendered in the eyebrow, e.g. "Events", "Weather". */
  category?: string;
  /** Date string rendered in the eyebrow, e.g. "May 21". */
  date?: string;
  /** Wraps the card in next/link when set. */
  href?: string;
  /**
   * Render the link as a plain `<a>` instead of next/link. Set this for
   * hrefs that leave the Next app (e.g. the editorial `/news` CMS), which
   * this app doesn't serve — a next/link there would RSC-prefetch and
   * client-navigate to a 404. A full-page `<a>` lets the CDN route it.
   */
  external?: boolean;
  /** Semantic heading level for the title. Defaults to 3. */
  headingLevel?: 2 | 3 | 4;
  className?: string;
};

export const NewsCard = ({
  title,
  excerpt,
  image,
  category,
  date,
  href,
  external,
  headingLevel = 3,
  className,
}: NewsCardProps) => {
  const hasEyebrow = Boolean(category || date);

  const body = (
    <>
      <div className="aspect-[4/3] w-full overflow-hidden rounded-md bg-muted">
        {image && (
          <img
            src={image}
            alt={title}
            loading="lazy"
            decoding="async"
            className="h-full w-full object-cover transition duration-300 group-hover:scale-[1.03]"
          />
        )}
      </div>
      <div className="mt-3">
        {hasEyebrow && (
          <div className="mb-1.5 flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">
            {category && <span className="text-primary">{category}</span>}
            {category && date && <span aria-hidden>·</span>}
            {date && <span>{date}</span>}
          </div>
        )}
        <Heading
          level={headingLevel}
          className="mb-1.5 font-heading text-lg font-bold leading-snug text-secondary group-hover:text-primary group-hover:underline underline-offset-2"
        >
          {title}
        </Heading>
        {excerpt && (
          <Text variant="default" className="line-clamp-2 text-sm text-muted-foreground">
            {excerpt}
          </Text>
        )}
      </div>
    </>
  );

  if (href) {
    const linkClass = cn('group block no-underline hover:no-underline', className);
    return external ? (
      <a href={href} className={linkClass}>
        {body}
      </a>
    ) : (
      <Link href={href} className={linkClass}>
        {body}
      </Link>
    );
  }

  return (
    <div className={cn('group block', className)}>
      {body}
    </div>
  );
};
